import React, { useRef } from 'react';
import { Box, Typography, Grid, Button } from '@mui/material';
import html2canvas from 'html2canvas';
import JsPDF from 'jspdf';
import { ReactComponent as BhimLogo } from '../../../public/assets/bhim.svg';
import { ReactComponent as UpiLogo } from '../../../public/assets/upi.svg';
import { ReactComponent as GooglePayLogo } from '../../../public/assets/googlepay.svg';
import { ReactComponent as PhonePeLogo } from '../../../public/assets/phonepe.svg';
import { ReactComponent as PaytmLogo } from '../../../public/assets/paytm.svg';
import { ReactComponent as AmazonPayLogo } from '../../../public/assets/amazonpay.svg';
import { ReactComponent as TimePayLogo } from '../../../public/assets/timepay.svg';
import { ReactComponent as WhatsappLogo } from '../../../public/assets/whatsapp.svg';
import { ReactComponent as DownloadIcon } from '../../../public/assets/downloadIcon.svg';
import { ReactComponent as ShareIcon } from '../../../public/assets/shareIcon.svg';


// JS QR code component
import QRCodeGenerator from './QRCodeGenerator';

export default function UjnScanAndPayCard({ qrString, merchantVpa, merchantName }) {
  const cardRef = useRef(null);

  const generatePDF = async () => {
    if (!cardRef.current) return null;

    const canvas = await html2canvas(cardRef.current, { scale: 2 });
    const imgData = canvas.toDataURL('image/png');

    const pdf = new JsPDF({
      orientation: 'portrait',
      unit: 'px',
      format: [canvas.width, canvas.height]
    });

    pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height);
    return pdf;
  };

  const handleDownloadPDF = async () => {
    const pdf = await generatePDF();
    if (pdf) pdf.save(`${merchantVpa || 'MerchantCard'}.pdf`);
  };

  const handleSharePDF = async () => {
    const pdf = await generatePDF();
    if (!pdf) return;

    const pdfBlob = pdf.output('blob');
    const file = new File([pdfBlob], `${merchantVpa || 'MerchantCard'}.pdf`, {
      type: 'application/pdf'
    });

    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({
          title: 'Merchant QR Card',
          text: 'Here is my QR card.',
          files: [file]
        });
      } catch (err) {
        console.error('Share failed:', err);
      }
    } else {
      // fallback for unsupported browsers
      pdf.save(`${merchantVpa || 'MerchantCard'}.pdf`);
      alert('Sharing is not supported on this device/browser. PDF downloaded instead.');
    }
  };

  return (
    <Box sx={{ textAlign: 'center', width: '75%', mx: 'auto' }}>
      <Box
        ref={cardRef}
        sx={{
          maxWidth: '100%',
          boxShadow: 3,
          textAlign: 'center',
          borderRadius: 2
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: 2,
            mb: 1,
            backgroundColor: '#056262',
            borderTopLeftRadius: 10, // Adjust 8 as needed
            borderTopRightRadius: 10
          }}
        >
          <img src="/assets/UJN/ujnpay.png" alt="Ujn Logo" style={{ height: 60, width: '65%' }} />
        </Box>

        {/* Scan & Pay */}
        <Typography
          variant="h6"
          sx={{
            mb: 1,
            fontStyle: 'italic',
            color: '#056262',
            fontWeight: 700
          }}
        >
          Scan & Pay
        </Typography>

        <QRCodeGenerator qrString={qrString} />
        <Typography variant="body1" sx={{ color: 'black', fontWeight: 600, fontFamily: 'Montserrat' }}>
          Merchant Name:{' '}
          <Box component="span" sx={{ fontWeight: 800 }}>
            {merchantName}
          </Box>
        </Typography>
        {/* UPI ID */}
        <Typography variant="body1" sx={{ color: 'black', fontWeight: 600, fontFamily: 'Montserrat' }}>
          UPI ID:{' '}
          <Box component="span" sx={{ fontWeight: 800 }}>
            {merchantVpa}
          </Box>
        </Typography>

        {/* BHIM + UPI Logos */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1, mb: 2 }}>
          <BhimLogo style={{ height: 50 }} />
          <UpiLogo style={{ height: 50 }} />
        </Box>

        {/* Payment Methods */}
        <Grid container spacing={2} justifyContent="center" mx={0.2}>
          {[
            { component: GooglePayLogo, name: 'Google Pay' },
            { component: PhonePeLogo, name: 'PhonePe' },
            { component: PaytmLogo, name: 'Paytm' },
            { component: AmazonPayLogo, name: 'Amazon Pay' },
            { component: TimePayLogo, name: 'Time Pay' },
            { component: WhatsappLogo, name: 'Whatsapp' }
          ].map(({ component: LogoComponent, name }, idx) => (
            <Grid
              item
              key={idx}
              xs={4}
              sm={4}
              md={4}
              display="flex"
              flexDirection="column"
              alignItems="center"
              px={2}
            >
              <LogoComponent style={{ height: 50, width: 60 }} />
              <Typography variant="caption" textAlign="center">
                {name}
              </Typography>
            </Grid>
          ))}
        </Grid>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: 2,
            mt: 2,
            backgroundColor: '#056262',
            borderBottomLeftRadius: 10,
            borderBottomRightRadius: 10
          }}
        >
          <img
            src="/assets/UJN/ujnpay_bottom.png"
            alt="Ujn Logo"
            style={{ height: 60, width: '90%' }}
          />
        </Box>
      </Box>

      {/* Buttons */}
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 2 }}>
        <Button
          variant="contained"
          onClick={handleDownloadPDF}
          endIcon={<DownloadIcon />}
          sx={{ p: 1, width: '50%' }}
          
        >
          Download
        </Button>
        <Button
          variant="outlined"
          onClick={handleSharePDF}
          endIcon={<ShareIcon sx={{ color: '#056262' }} />}
          sx={{ p: 1, width: '50%' }}
        >
          Share
        </Button>
      </Box>
    </Box>
  );
}
